/**
 * environmentalApi.js — API functions for the Environmental Report page.
 *
 * fetchEnvironmentalReport       — runs the environmental report query
 * fetchEnvironmentalFilterOptions — loads filter option sets
 */

import { postReport } from '../../../../services/api.js';
import { fetchReasonCodeOptions } from '../../../../utils/reasonCodeOptions.js';
import { reportFilterFields } from '../../../../utils/reportRequestBody.js';
import { fetchPortalFilters, fetchEventTypeOptions } from '../../../../utils/reportFilterOptions.js';


/** Device category filter auto-set by environment type */
function getDeviceCategoryFilter(environTypeId) {
    if (environTypeId === 1) return '1,17';   // Temperature
    if (environTypeId === 2) return '17';      // Humidity
    return '1';                                // Battery (3 & 4)
}

// ---------------------------------------------------------------------------
// Fetch Environmental Report
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — YYYY-MM-DD
 * @param {string} opts.endDate     — YYYY-MM-DD
 * @param {number} opts.environTypeId — 1=Temp, 2=Humidity, 3=CustomBattery, 4=FullBattery
 * @param {object} opts.filters
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchEnvironmentalReport({ campusId, username, startDate, endDate, environTypeId, filters = {} }) {
    const body = {
        rep_type: 'environment_report',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        device_category_filter: getDeviceCategoryFilter(environTypeId),
        ...reportFilterFields(filters, { omit: ['device_category_filter', 'device_filter', 'resident_group_filter', 'area_filter', 'area_group_filter', 'user_group_filter'] }),
        resident_group_filter: '',
        ...reportFilterFields(filters, { omit: ['device_category_filter', 'device_filter', 'area_type_filter', 'reason_filter', 'user_filter', 'event_type_filter', 'resident_filter', 'resident_group_filter', 'area_group_filter', 'user_group_filter'] }),
        area_group_filter: '',
        user_group_filter: '',
        environment_type: environTypeId,
    };

    const res = await postReport(body, '[environmentalApi] fetchEnvironmentalReport error');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    /* getEnvironmentReport returns summary as { count, records }, not an array, so passing
       it through left every environmental report's Summary view on "No data". */
    const summary = Array.isArray(data?.summary) ? data.summary : (data?.summary?.records || []);
    return {
        records: data?.records || [],
        summary,
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------

/**
 * Environmental's option subset: no groups or device categories (the category is set by the
 * report type), and reasons without the synthetic "No Reason" row (the environment procs
 * cannot filter on it).
 */
export async function fetchEnvironmentalFilterOptions(campusId) {
    try {
        const [portal, eventTypes, reasonCodes] = await Promise.all([
            fetchPortalFilters(campusId, ['areas', 'areaTypes', 'residents', 'users', 'deviceCategories']),
            fetchEventTypeOptions(campusId),
            fetchReasonCodeOptions(campusId, { includeNoReason: false }),
        ]);
        return {
            areas: portal.areas || [],
            areaTypes: portal.areaTypes || [],
            residents: portal.residents || [],
            users: portal.users || [],
            eventTypes,
            reasonCodes,
        };
    } catch (err) {
        console.error('[environmentalApi] fetchEnvironmentalFilterOptions error:', err);
        return {};
    }
}
